/**
 * Grid Experience Card Component
 * Card for displaying Nexgo experiences in grid layout (experiences tab)
 * Matches Figma design: 220px width, image with badge overlay, details, and price
 */

import React, { useRef } from 'react';
import {
  spacing,
  borders,
  typography,
  fontPrimary,
  typographyColors,
  strokeColors,
  backgroundColors,
} from '@/design-system/utils';
import { ExperienceCard as ExperienceCardType } from '@/constants/chatScreenData';
import { getExperienceTypeLabel } from '@/utils/experienceLabels';
import CurrencyDisplay from '@/components/common/CurrencyDisplay';
import type { Swiper as SwiperType } from 'swiper';
import { ExperienceCardImageSwiper } from './ExperienceCardImageSwiper';
import { ExperienceTypeIcon } from './icons/ExperienceTypeIcon';
import { Star } from 'lucide-react';
import { useCardImagePreloader } from '@/hooks/useImagePreloader';

export interface GridExperienceCardProps {
  experience: ExperienceCardType & {
    rating?: number;
    ratingCount?: number;
    description?: string;
    price?: string;
    currencySymbol?: string; // Currency symbol (e.g., '₹', '$')
    currency?: string; // Currency code (e.g., 'USD', 'INR') - preferred over currencySymbol
    backendConversionRate?: number; // Conversion rate from backend for currency conversion
    duration?: string | { days?: number; nights?: number; hours?: number };
    attractionsCount?: number;
    days?: number;
    nights?: number;
    locationsCount?: number;
    tags?: string[]; // Category tags like ['Adventure', 'Festival', 'Spiritual', 'Music', 'Yoga']
    images?: string[]; // Array of all images for carousel
  };
  onClick?: () => void;
}

// Category-specific colors from Figma
const categoryColors = {
  activities: {
    badgeOffset: '#fed7aa',
    badgeSolid: '#fdba74',
    priceBackground: '#fff7ed',
  },
  signature: {
    badgeOffset: '#e9d5ff', // Light purple background (from Figma: Colors/Category/Signature/Offset)
    badgeSolid: '#d8b4fe', // Dark purple for icon and border (from Figma: Colors/Category/Signature/Solid)
    priceBackground: '#faf5ff',
  },
  package: {
    badgeOffset: '#a5f3fc',
    badgeSolid: '#67e8f9',
    priceBackground: '#ecfeff',
  },
  'riders-corner': {
    badgeOffset: '#fed7aa',
    badgeSolid: '#fdba74',
    priceBackground: '#fff7ed',
  },
  stays: {
    badgeOffset: '#d9f99d',
    badgeSolid: '#bef264',
    priceBackground: '#f7fee7',
  },
};

export const GridExperienceCard: React.FC<GridExperienceCardProps> = ({
  experience,
  onClick,
}) => {
  const [isSwiping, setIsSwiping] = React.useState(false);
  const swiperRef = React.useRef<SwiperType | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const categoryColor =
    categoryColors[experience.type] || categoryColors.activities;

  const formatDuration = (
    duration:
      | string
      | { days?: number; nights?: number; hours?: number }
      | undefined
      | null
  ) => {
    // Handle null/undefined
    if (!duration) {
      return '';
    }

    // For activities, always format in hours/minutes, never convert to days
    const isActivity = experience.type === 'activities';

    // Handle duration object (e.g., { days: 6, nights: 5 })
    if (typeof duration === 'object' && duration !== null) {
      const durationObj = duration as {
        days?: number;
        nights?: number;
        hours?: number;
      };

      // For activities with hours, always format in hours/minutes
      if (
        isActivity &&
        durationObj.hours !== undefined &&
        durationObj.hours !== null
      ) {
        const totalMinutes = durationObj.hours;
        if (totalMinutes >= 60) {
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          return `${hours} hr${hours > 1 ? 's' : ''}${
            minutes !== 0 ? ` ${minutes} min${minutes > 1 ? 's' : ''}` : ''
          }`;
        }
        return `${totalMinutes} min${totalMinutes > 1 ? 's' : ''}`;
      }

      // For non-activities, prefer days/nights format
      if (
        !isActivity &&
        durationObj.days !== undefined &&
        durationObj.days !== null
      ) {
        const days = durationObj.days;
        if (durationObj.nights !== undefined && durationObj.nights !== null) {
          return `${days} ${days === 1 ? 'Day' : 'Days'} ${durationObj.nights} ${durationObj.nights === 1 ? 'Night' : 'Nights'}`;
        }
        return `${days} ${days === 1 ? 'Day' : 'Days'}`;
      }

      // For non-activities with hours, convert to days if >= 24
      if (
        !isActivity &&
        durationObj.hours !== undefined &&
        durationObj.hours !== null
      ) {
        const hours = durationObj.hours;
        if (hours >= 24) {
          const days = Math.floor(hours / 24);
          const remainingHours = hours % 24;
          if (remainingHours === 0) {
            return `${days} ${days === 1 ? 'Day' : 'Days'}`;
          }
          return `${days} ${days === 1 ? 'Day' : 'Days'} ${remainingHours}h`;
        }
        return `${hours}h`;
      }

      // If object has no valid duration fields, return empty
      return '';
    }

    // Handle string duration
    if (typeof duration === 'string') {
      const durationStr = duration.trim();
      if (!durationStr) {
        return '';
      }

      // For activities, if string contains hours/minutes format, return as-is
      // Otherwise parse and format correctly
      if (isActivity) {
        // Check if already in hours/minutes format (e.g., "2 hrs 30 mins" or "45 mins")
        const hoursMinsMatch = durationStr.match(
          /(\d+)\s*hrs?\s*(?:(\d+)\s*mins?)?/i
        );
        if (hoursMinsMatch) {
          return durationStr; // Return as-is if already formatted
        }

        // Check if it's just minutes (e.g., "45 mins")
        const minsOnlyMatch = durationStr.match(/(\d+)\s*mins?/i);
        if (minsOnlyMatch) {
          return durationStr; // Return as-is
        }

        // If it's in "X Hours" format, convert to hours/minutes
        const hoursMatch = durationStr.match(/(\d+)\s+Hours?/i);
        if (hoursMatch) {
          const totalMinutes = parseInt(hoursMatch[1], 10) * 60;
          if (totalMinutes >= 60) {
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            return `${hours} hr${hours > 1 ? 's' : ''}${
              minutes !== 0 ? ` ${minutes} min${minutes > 1 ? 's' : ''}` : ''
            }`;
          }
          return `${totalMinutes} min${totalMinutes > 1 ? 's' : ''}`;
        }

        // If it's in "Xh" format, convert to hours/minutes
        const hMatch = durationStr.match(/(\d+)h/i);
        if (hMatch) {
          const totalMinutes = parseInt(hMatch[1], 10) * 60;
          if (totalMinutes >= 60) {
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            return `${hours} hr${hours > 1 ? 's' : ''}${
              minutes !== 0 ? ` ${minutes} min${minutes > 1 ? 's' : ''}` : ''
            }`;
          }
          return `${totalMinutes} min${totalMinutes > 1 ? 's' : ''}`;
        }

        // If it's a number (minutes), format it
        const numMatch = durationStr.match(/^(\d+)$/);
        if (numMatch) {
          const totalMinutes = parseInt(numMatch[1], 10);
          if (totalMinutes >= 60) {
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            return `${hours} hr${hours > 1 ? 's' : ''}${
              minutes !== 0 ? ` ${minutes} min${minutes > 1 ? 's' : ''}` : ''
            }`;
          }
          return `${totalMinutes} min${totalMinutes > 1 ? 's' : ''}`;
        }

        // Return as-is if no patterns match (might already be formatted correctly)
        return durationStr;
      }

      // For non-activities, handle days format
      const daysMatch = durationStr.match(/(\d+)\s+Days?/i);
      if (daysMatch) {
        const days = parseInt(daysMatch[1], 10);
        return `${days} ${days === 1 ? 'Day' : 'Days'}`;
      }

      // Handle "X Hours" format for non-activities (convert to days if >= 24)
      const hoursMatch = durationStr.match(/(\d+)\s+Hours?/i);
      if (hoursMatch) {
        const hours = parseInt(hoursMatch[1], 10);
        if (hours >= 24) {
          const days = Math.floor(hours / 24);
          const remainingHours = hours % 24;
          if (remainingHours === 0) {
            return `${days} ${days === 1 ? 'Day' : 'Days'}`;
          }
          return `${days} ${days === 1 ? 'Day' : 'Days'} ${remainingHours}h`;
        }
        return `${hours}h`;
      }

      // Handle "Xh" format for non-activities
      const hMatch = durationStr.match(/(\d+)h/i);
      if (hMatch) {
        const hours = parseInt(hMatch[1], 10);
        if (hours >= 24) {
          const days = Math.floor(hours / 24);
          const remainingHours = hours % 24;
          if (remainingHours === 0) {
            return `${days} ${days === 1 ? 'Day' : 'Days'}`;
          }
          return `${days} ${days === 1 ? 'Day' : 'Days'} ${remainingHours}h`;
        }
        return `${hours}h`;
      }

      return durationStr; // Return as is if no patterns match
    }

    // Fallback for any other type
    return '';
  };
  // Get all images for carousel (use images array if available, otherwise fallback to single image)
  // Normalize images to ensure they're always string arrays
  const normalizeImages = (imgArray: unknown): string[] => {
    if (!imgArray) return [];
    if (Array.isArray(imgArray)) {
      return imgArray.filter(
        (item): item is string => typeof item === 'string' && item.length > 0
      );
    }
    if (typeof imgArray === 'string' && imgArray.length > 0) {
      return [imgArray];
    }
    return [];
  };

  const imagesArray = normalizeImages(experience.images);
  const imageString =
    typeof experience.image === 'string' ? experience.image : '';

  const images =
    imagesArray.length > 0 ? imagesArray : imageString ? [imageString] : [];

  // Preload only first image when card enters viewport (remaining images load on hover)
  useCardImagePreloader(cardRef, images.slice(0, 1), {
    rootMargin: '200px', // Start preloading when card is 200px away
    maxConcurrent: 1, // Only preload first image
  });

  // Handle Swiper touch events to detect swiping
  const handleTouchStart = () => {
    setIsSwiping(false);
  };

  const handleTouchMove = () => {
    setIsSwiping(true);
  };

  const handleTouchEnd = () => {
    // Reset after a short delay to allow click handlers to check the flag
    setTimeout(() => setIsSwiping(false), 150);
  };

  // Handle hover to start/stop autoplay
  const handleMouseEnter = () => {
    if (swiperRef.current && images.length > 1) {
      // Immediately slide to next image for instant feedback
      swiperRef.current.slideNext();
      swiperRef.current.autoplay?.start();
    }
  };

  const handleMouseLeave = () => {
    if (swiperRef.current) {
      swiperRef.current.autoplay?.stop();
    }
  };

  // Get numeric price from string or number
  const getNumericPrice = (price?: string | number): number => {
    if (price === undefined || price === null) return 0;

    // If already a number, return it
    if (typeof price === 'number') {
      return isNaN(price) ? 0 : price;
    }

    // If string, remove currency symbols and parse
    const cleaned = price.replace(/[₹$,]/g, '').trim();
    const numValue = parseFloat(cleaned);
    return isNaN(numValue) ? 0 : numValue;
  };

  // Get backend currency - prefer currency field over currencySymbol inference
  const getBackendCurrency = (): string => {
    // Prefer the currency field (e.g., "USD", "INR") as it's more reliable
    if (experience.currency && typeof experience.currency === 'string') {
      return experience.currency.toUpperCase();
    }
    // Fallback to currencySymbol inference
    const symbol = experience.currencySymbol;
    if (!symbol) return 'USD';
    if (symbol === '₹' || symbol === 'INR') return 'INR';
    if (symbol === '$' || symbol === 'USD') return 'USD';
    // Default to USD for unknown symbols
    return 'USD';
  };

  const numericPrice = getNumericPrice(experience.price);
  const backendCurrency = getBackendCurrency();

  // Get badge label based on type - use centralized labels
  const getBadgeLabel = () => {
    return getExperienceTypeLabel(experience.type);
  };

  // Get price suffix based on category
  const getPriceSuffix = () => {
    return experience.type === 'stays' ? '/person/night' : '/person';
  };

  // Default tags if not provided - filter out empty strings
  const rawTags = experience.tags || [
    'Adventure',
    'Festival',
    'Spiritual',
    'Music',
    'Yoga',
  ];
  const defaultTags = rawTags.filter(tag => tag && tag.trim().length > 0);

  // Handle card click - prevent if user was swiping
  const handleCardClick = (e: React.MouseEvent) => {
    if (isSwiping) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick?.();
  };

  return (
    <div
      ref={cardRef}
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        display: 'flex',
        backgroundColor: backgroundColors.primary,
        border: `1px solid ${strokeColors.light}`,
        borderRadius: borders.radius.xl,
        padding: spacing[2], // 8px
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        flexDirection: 'column',
        gap: spacing[1.5], // 6px
        boxShadow: '0px 4px 12px 0px rgba(0,0,0,0.06)',
        boxSizing: 'border-box',
      }}
      className='w-full'
    >
      {/* Image Container */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '160px',
          borderRadius: borders.radius.lg,
          overflow: 'hidden',
        }}
      >
        {/* Image Carousel with Swiper */}
        {images.length > 0 ? (
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              borderRadius: borders.radius.lg,
              overflow: 'hidden',
            }}
          >
            <ExperienceCardImageSwiper
              images={images}
              title={experience.title}
              swiperRef={swiperRef}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              sizes='(max-width: 768px) 100vw, 220px'
              className='experience-card-swiper'
            />
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: backgroundColors.secondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <p
              style={{
                color: typographyColors.text,
                fontSize: typography.size.sm,
              }}
            >
              No Image
            </p>
          </div>
        )}

        {/* Badge Overlay */}
        <div
          style={{
            position: 'absolute',
            top: spacing[1],
            left: spacing[1],
            zIndex: 2,
          }}
        >
          <div
            style={{
              backgroundColor: categoryColor.badgeOffset,
              border: `1px solid ${categoryColor.badgeSolid}`,
              borderRadius: borders.radius.lg,
              paddingLeft: spacing[1],
              paddingRight: spacing[2],
              paddingTop: spacing[1],
              paddingBottom: spacing[1],
              display: 'flex',
              gap: spacing[1],
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                backgroundColor:
                  experience.type === 'signature'
                    ? 'transparent' // No background for signature icon
                    : categoryColor.badgeSolid,
                borderRadius: borders.radius.md,
                padding: spacing[0.5],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '12px',
                height: '12px',
              }}
            >
              <ExperienceTypeIcon type={experience.type} size={12} />
            </div>
            <p
              style={{
                fontFamily: fontPrimary,
                fontSize: '10px',
                fontWeight: typography.weight.bold,
                color: typographyColors.primary, // Primary dark color for all badge labels
                lineHeight: 'normal',
                margin: 0,
              }}
            >
              {getBadgeLabel()}
            </p>
          </div>
        </div>
        {/* Attractions Count & Duration Overlay */}
        {(() => {
          // Stay experiences should not show duration
          const isStayExperience = experience.type === 'stays';

          // Check if we have valid duration to display (skip for stay experiences)
          const duration = isStayExperience ? null : experience.duration;
          const hasValidDuration =
            !isStayExperience &&
            duration &&
            (typeof duration === 'string' ||
              (typeof duration === 'object' &&
                duration !== null &&
                ('days' in duration ||
                  'hours' in duration ||
                  'nights' in duration)));

          const formattedDuration = hasValidDuration
            ? formatDuration(duration)
            : '';
          const hasAttractions =
            typeof experience.attractionsCount === 'number' &&
            experience.attractionsCount > 0;

          // Only show overlay if we have duration or attractions to display
          if (!formattedDuration && !hasAttractions) {
            return null;
          }

          return (
            <div
              style={{
                position: 'absolute',
                bottom: spacing[4],
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2,
                backgroundColor: '#00000080',
                borderRadius: borders.radius.lg,
                padding: `2px ${spacing[2]}`,
                display: 'flex',
                gap: spacing[2],
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                minWidth: '90%',
              }}
            >
              <span
                style={{
                  fontSize: '10px',
                  color: '#FFFFFF',
                  fontWeight: typography.weight.bold,
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing[1],
                }}
              >
                {formattedDuration && <span>{formattedDuration}</span>}
                {formattedDuration && hasAttractions && (
                  <span style={{ opacity: 0.5 }}>|</span>
                )}
                {hasAttractions && (
                  <span>
                    {experience.attractionsCount}{' '}
                    {experience.attractionsCount === 1
                      ? 'Attraction'
                      : 'Attractions'}
                  </span>
                )}
              </span>
            </div>
          );
        })()}
      </div>

      {/* Content Container */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[1.5],
          paddingLeft: spacing[1],
          paddingRight: spacing[1],
          paddingTop: spacing[1.5],
          paddingBottom: spacing[1.5],
        }}
      >
        {/* Categories - Above Title - Show only 1, then "+X more" */}
        {defaultTags && defaultTags.length > 0 && (
          <div
            style={{
              display: 'flex',
              gap: spacing[1],
              alignItems: 'center',
              justifyContent: 'flex-start',
              flexWrap: 'wrap',
            }}
          >
            {defaultTags.slice(0, 1).map((tag, index) => (
              <React.Fragment key={index}>
                <span
                  style={{
                    fontSize: typography.size.xs,
                  }}
                >
                  <Star
                    size={8}
                    fill='#000000'
                    stroke='#000000'
                    strokeWidth={1}
                  />
                </span>
                <p
                  style={{
                    fontFamily: fontPrimary,
                    fontSize: typography.size.xs,
                    fontWeight: typography.weight.regular,
                    color: typographyColors.placeholder, // Light grey color
                    lineHeight: 'normal',
                    margin: 0,
                  }}
                >
                  {tag}
                </p>
              </React.Fragment>
            ))}
            {defaultTags.length > 1 && (
              <p
                style={{
                  fontFamily: fontPrimary,
                  fontSize: typography.size.xs,
                  fontWeight: typography.weight.regular,
                  color: typographyColors.placeholder,
                  lineHeight: 'normal',
                  margin: 0,
                }}
              >
                +{defaultTags.length - 1} more
              </p>
            )}
          </div>
        )}

        {/* Title and Location */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[1],
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[1],
            }}
          >
            <p
              style={{
                fontFamily: fontPrimary,
                fontSize: '18px',
                fontWeight: typography.weight.semibold,
                color: typographyColors.primary,
                lineHeight: '1.4',
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                whiteSpace: 'pre-wrap',
                minHeight: '50.4px', // 18px * 1.4 * 2 lines = 50.4px
              }}
            >
              {experience.title || 'Experience'}
            </p>
            <div
              style={{
                display: 'flex',
                gap: spacing[1],
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flex: '1 0 0',
                  alignItems: 'flex-end',
                  minWidth: 0, // Allow flex item to shrink
                  overflow: 'hidden', // Prevent overflow
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: spacing[1],
                    alignItems: 'center',
                    padding: 0,
                    borderRadius: borders.radius.xl,
                    minWidth: 0, // Allow flex item to shrink
                    overflow: 'hidden', // Prevent overflow
                    maxWidth: '100%', // Ensure it doesn't exceed parent
                    width: '100%', // Take full available width
                  }}
                >
                  <svg
                    width='12'
                    height='12'
                    viewBox='0 0 20 20'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    style={{
                      flexShrink: 0, // Prevent icon from shrinking
                    }}
                  >
                    <path
                      d='M10 2.5C7.23858 2.5 5 4.73858 5 7.5C5 11.25 10 17.5 10 17.5C10 17.5 15 11.25 15 7.5C15 4.73858 12.7614 2.5 10 2.5Z'
                      stroke={typographyColors.secondary}
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <circle
                      cx='10'
                      cy='7.5'
                      r='2.5'
                      stroke={typographyColors.secondary}
                      strokeWidth='1.5'
                    />
                  </svg>
                  <p
                    style={{
                      fontFamily: fontPrimary,
                      fontSize: typography.size.xs,
                      fontWeight: typography.weight.medium,
                      color: typographyColors.secondary,
                      lineHeight: 'normal',
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0, // Allow text to shrink
                      maxWidth: '100%', // Ensure it doesn't exceed parent
                      flex: '1 1 auto', // Take available space
                    }}
                  >
                    {experience.location}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Description - Only show if available */}
          {experience.description && (
            <p
              style={{
                fontFamily: fontPrimary,
                fontSize: typography.size.xs,
                fontWeight: typography.weight.regular,
                color: typographyColors.placeholder,
                lineHeight: '1.4',
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                whiteSpace: 'pre-wrap',
                minHeight: '33.6px', // 12px (xs) * 1.4 * 2 lines = 33.6px
              }}
            >
              {experience.description}
            </p>
          )}
        </div>

        {/* Price - Hide for riders-corner */}
        {experience.type !== 'riders-corner' && (
          <div
            style={{
              backgroundColor: categoryColor.priceBackground,
              borderRadius: borders.radius.lg,
              padding: `${spacing[3]} 0`, // 12px vertical padding, 0 horizontal per Figma
              display: 'flex',
              gap: spacing[1],
              alignItems: 'center',
              justifyContent: 'center', // Center content per Figma
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              minWidth: 0,
              overflow: 'hidden',
              lineHeight: 'normal',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: spacing[0],
                alignItems: 'end', // Align items to baseline for proper text alignment
                fontWeight: typography.weight.bold,
                color: typographyColors.primary,
                flexShrink: 0,
              }}
            >
              {numericPrice > 0 ? (
                <div
                  style={{
                    fontFamily: fontPrimary,
                    fontWeight: typography.weight.bold,
                    color: typographyColors.primary,
                    lineHeight: 'normal',
                  }}
                >
                  <CurrencyDisplay
                    amount={numericPrice}
                    backendCurrency={backendCurrency}
                    backendConversionRate={experience.backendConversionRate}
                    showDecimals={false}
                    className=''
                    symbolClassName=''
                    amountClassName=''
                  />
                </div>
              ) : (
                <p
                  style={{
                    fontFamily: fontPrimary,
                    fontSize: typography.size.xs,
                    fontWeight: typography.weight.bold,
                    color: typographyColors.primary,
                    lineHeight: 'normal',
                    margin: 0,
                    flexShrink: 0,
                  }}
                >
                  Price on request
                </p>
              )}
            </div>
            <p
              style={{
                fontFamily: fontPrimary,
                fontSize: typography.size.xs,
                fontWeight: typography.weight.regular,
                color: typographyColors.secondary, // Secondary text color per Figma
                lineHeight: 'normal',
                margin: 0,
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              {getPriceSuffix()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
